/**
 * A "Nova consulta" ligada ao back (#1011, fase 2 da agenda da clínica): lê as
 * vagas, as listas do formulário e o paciente vinculado, e agenda no SISAC com
 * o formulário inteiro. Devolve o que `NovaConsulta` mostra.
 *
 * ── A ordem das chamadas ─────────────────────────────────────────────────
 *
 * "Ver vagas" confirma o médico e lê as vagas dele (`POST /v1/consultas/leitura`
 * com a finalidade `vagas`), levando a oportunidade aberta: o servidor confere
 * a carteira (ADR-0028). As listas do formulário (convênios, planos do
 * convênio, origens, locais) vão pela mesma rota. "Agendar" grava a escolha na
 * ficha (`PUT .../consulta`), se ela mudou, e só então agenda
 * (`POST .../consulta/agendar`): o agendar não grava médico nem especialidade.
 * Se ele falhar numa consulta sem marcação, a ficha fica com o médico novo e
 * sem marcação, que é o estado de antes. Numa já marcada, a troca se desfaz:
 * sem isso, a consulta antiga apareceria com o médico novo.
 *
 * ── O paciente vem do vínculo ────────────────────────────────────────────
 *
 * Quem procura e vincula o paciente é o bloco "Paciente no SISAC" (pelas rotas
 * do cliente: CPF, nome ou código). Vinculado, a consulta passa a trazer o
 * código (`paciente_vinculado`), e aqui se lê o cadastro dele por
 * `GET .../consulta/paciente`, em que o servidor tira o código do vínculo: é o
 * que pré-preenche o formulário. Sem cadastro no SISAC, o paciente vai só pelo
 * nome digitado, sem código.
 *
 * ── Um agendamento, uma chave ────────────────────────────────────────────
 *
 * O agendar exige `Idempotency-Key`. A chave nasce no primeiro envio de cada
 * vaga e volta no "Tentar de novo" da mesma vaga: se a primeira resposta se
 * perdeu, o servidor devolve a gravada em vez de marcar outra vez.
 *
 * ── Os dois 409 de versão ────────────────────────────────────────────────
 *
 * Os dois chegam como `CONFLITO_DE_VERSAO`. O da ficha é outra pessoa que
 * salvou antes; o do agendar é a vaga que alguém pegou. Por isso o erro de
 * gravar a ficha vem embrulhado.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type ConsultaDaOportunidade, type OpcoesDaConsulta } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import type { CalendarioPaginado } from '@/shared/ui';
import { usePreferencias } from '@/shared/sessao/contexto';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { consultaMarcada } from '../lib/consulta-da-oportunidade';
import type {
  EstadoDasVagas,
  EstadoDoEnvio,
  ListaDoFormulario,
  PacienteDaConsulta,
} from '../lib/consultas-na-tela';
import { recadoDaEscrita } from '../lib/consultas-na-tela';
import {
  opcoesDaLista,
  parametrosDoAgendamento,
  type FormularioDoAgendamento,
} from '../lib/formulario-do-agendamento';
import {
  instanteDaVaga,
  diasComVagaDas,
  MESES_A_FRENTE,
  mesDe,
  mesesDepois,
  parametrosDasVagas,
  parametrosDoDia,
  parametrosDoMes,
  vagasDas,
  vagasNaTela,
  type ListaDaAgenda,
  type OperacoesDaAgenda,
} from '../lib/marcar-no-sisac';
import type { EscolhaDoMedico } from '../ui/nova-consulta';

type Opcao = OpcoesDaConsulta['medicos'][number];
type Escolha = { readonly medico: Opcao; readonly especialidade: Opcao };

const SISAC_FORA = 'O SISAC não respondeu. Tente de novo em instantes.';

/** O erro de gravar a escolha na ficha, separado do de agendar. */
class FichaNaoGravada extends Error {
  constructor(readonly causa: unknown) {
    super('Não foi possível gravar o médico na ficha. Nada foi agendado.');
  }
}

const foraDoAr = (erro: unknown) => erro instanceof ErroDaApi && erro.status === 503;
const conflito = (erro: unknown) =>
  erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_VERSAO';
const divergente = (erro: unknown) =>
  erro instanceof ErroDaApi && erro.codigo === 'IDEMPOTENCIA_DIVERGENTE';

const recadoDaLeitura = (erro: unknown, generica: string) =>
  foraDoAr(erro) ? SISAC_FORA : recadoDeErro(erro, { generica });

const novaChave = () =>
  globalThis.crypto?.randomUUID?.() ??
  `agendar-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Uma lista do formulário pela leitura genérica, a partir da oportunidade: o
 * servidor confere a carteira. Sem a operação, não pergunta nada.
 */
export function useListaDaAgenda(
  oportunidadeId: string,
  operacao: string | undefined,
  lista: ListaDaAgenda | 'motivos_de_cancelamento',
  parametros: Record<string, string>,
) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.listaDaAgenda(lista, Object.values(parametros).join('|')),
    enabled: api !== null && operacao !== undefined,
    // Cadastros da clínica mudam pouco: cinco minutos sem reler.
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      const r = await api!.consultarSisac({
        corpo: { oportunidade_id: oportunidadeId, operacao_id: operacao!, parametros },
        sinal: signal,
      });
      return opcoesDaLista(r.linhas);
    },
  });
}

export function useNovaConsulta({
  oportunidadeId,
  consulta,
  operacoes,
  opcoes,
  aoMarcar,
}: {
  oportunidadeId: string;
  consulta: ConsultaDaOportunidade;
  operacoes: OperacoesDaAgenda;
  /** As opções com o código do SISAC: é ele que filtra as vagas. */
  opcoes: { readonly medicos: readonly Opcao[]; readonly especialidades: readonly Opcao[] };
  /** Gravou no sistema da clínica: com o que a conferência disse (#1187). */
  aoMarcar: (conferencia?: ConsultaDaOportunidade['conferencia']) => void;
}) {
  const api = useApi();
  const cache = useQueryClient();
  // Os padrões do formulário, da tela "Agenda da clínica" (#1187).
  const padroes = usePreferencias().clinica_padroes;
  const [escolha, definirEscolha] = useState<Escolha | null>(null);
  const [ocupada, definirOcupada] = useState(false);
  const [convenio, definirConvenio] = useState('');
  // Por mês (#1187): o mês aberto no calendário e o dia aberto nele.
  const porMes = operacoes.dias_com_vaga !== undefined;
  const [mes, definirMes] = useState(() => mesDe(new Date()));
  const [dia, definirDia] = useState<string | null>(null);
  const chavesDoAgendamento = useRef(new Map<string, string>());
  const codigoVinculado = consulta.paciente_vinculado;
  /*
   * Remarcar (#1187): a consulta marcada, com a parte ligada. O "Editar" usa as
   * mesmas 3 etapas; o horário de agora vale como vaga, e salvar remarca.
   */
  const vagaAtual = consulta.agendamento?.codigo_da_agenda ?? null;
  // Só com o paciente pelo código: sem ele, o SISAC não confere a vaga de antes (#1187).
  const remarcando =
    operacoes.remarcar !== undefined &&
    consultaMarcada(consulta) &&
    vagaAtual !== null &&
    consulta.agendamento?.codigo_do_paciente != null;
  const lido = useQuery({
    queryKey: chaves.agendamentoDaConsulta(oportunidadeId),
    enabled: api !== null && remarcando && operacoes.ler_agendamento !== undefined,
    queryFn: ({ signal }) =>
      api!.agendamentoDaConsulta({ caminho: { id: oportunidadeId }, sinal: signal }),
  });

  const vinculado = useQuery({
    queryKey: chaves.pacienteVinculadoDaConsulta(oportunidadeId, codigoVinculado ?? ''),
    enabled: api !== null && codigoVinculado !== null,
    gcTime: 0,
    queryFn: ({ signal }) =>
      api!.pacienteVinculadoDaConsulta({ caminho: { id: oportunidadeId }, sinal: signal }),
  });

  const paciente: PacienteDaConsulta =
    codigoVinculado === null
      ? { situacao: 'sem-vinculo' }
      : vinculado.isPending
        ? { situacao: 'lendo' }
        : // Sem a leitura ligada, com o SISAC fora ou com o paciente sumido, vai só o código.
          {
            situacao: 'vinculado',
            paciente: {
              codigo: codigoVinculado,
              nome: vinculado.data?.paciente?.nome ?? 'Paciente vinculado ao contato',
              finalDoCpf: vinculado.data?.paciente?.cpf_final ?? null,
            },
            dados: vinculado.data?.paciente?.dados ?? null,
          };

  const convenios = useListaDaAgenda(oportunidadeId, operacoes.convenios, 'convenios', {});
  const planos = useListaDaAgenda(
    oportunidadeId,
    convenio === '' ? undefined : operacoes.planos,
    'planos',
    { cod_convenio: convenio },
  );
  const origens = useListaDaAgenda(oportunidadeId, operacoes.origens, 'origens', {});
  const locais = useListaDaAgenda(oportunidadeId, operacoes.locais, 'locais', {});

  const estadoDaLista = (
    lista: ListaDaAgenda,
    leitura: typeof convenios,
    esperando = false,
  ): ListaDoFormulario =>
    operacoes[lista] === undefined
      ? { situacao: 'desligada' }
      : esperando
        ? { situacao: 'pronta', opcoes: [] }
        : leitura.isError
          ? {
              situacao: 'erro',
              recado: recadoDaLeitura(leitura.error, 'Não foi possível ler a lista no SISAC.'),
            }
          : leitura.data
            ? { situacao: 'pronta', opcoes: leitura.data }
            : { situacao: 'carregando' };

  /**
   * Devolve à ficha o médico e a especialidade de antes da troca, na versão que
   * a troca deixou. Se nem isso der, relê a consulta, para a tela mostrar o que
   * o servidor tem.
   */
  async function desfazerATroca(antes: ConsultaDaOportunidade, trocada: ConsultaDaOportunidade) {
    try {
      const devolvida = await api!.salvarConsultaDaOportunidade({
        caminho: { id: oportunidadeId },
        corpo: {
          medico_id: antes.medico?.id ?? null,
          especialidade_id: antes.especialidade?.id ?? null,
          consulta_em: antes.consulta_em,
          versao: trocada.versao,
        },
      });
      cache.setQueryData(chaves.consultaDaOportunidade(oportunidadeId), devolvida);
    } catch {
      void cache.invalidateQueries({ queryKey: chaves.consultaDaOportunidade(oportunidadeId) });
    }
  }

  const medicoId = escolha?.medico.id ?? '';
  const diasDoMes = useQuery({
    queryKey: chaves.diasComVaga(medicoId, mes),
    enabled: api !== null && escolha !== null && porMes,
    queryFn: async ({ signal }) => {
      const r = await api!.consultarSisac({
        corpo: {
          oportunidade_id: oportunidadeId,
          operacao_id: operacoes.dias_com_vaga!,
          parametros: parametrosDoMes(escolha!.medico.codigo, mes, new Date()),
        },
        sinal: signal,
      });
      return diasComVagaDas(r.linhas);
    },
  });
  // Por mês, as vagas são só as do dia aberto; sem dia aberto, nenhuma é pedida.
  const vagas = useQuery({
    queryKey: porMes
      ? chaves.vagasDaConsulta(medicoId, dia ?? '')
      : chaves.vagasDaConsulta(medicoId),
    enabled: api !== null && escolha !== null && (!porMes || dia !== null),
    queryFn: async ({ signal }) => {
      const r = await api!.consultarSisac({
        corpo: {
          oportunidade_id: oportunidadeId,
          operacao_id: operacoes.vagas,
          parametros: porMes
            ? parametrosDoDia(escolha!.medico.codigo, dia!, new Date())
            : parametrosDasVagas(escolha!.medico.codigo, new Date()),
        },
        sinal: signal,
      });
      return { lista: vagasDas(r.linhas), cortada: r.cortada };
    },
  });
  const recarregarVagas = () => {
    if (porMes) void diasDoMes.refetch();
    if (!porMes || dia !== null) void vagas.refetch();
  };

  const marcar = useMutation({
    mutationFn: async ({
      vagaId,
      formulario,
    }: {
      vagaId: string;
      formulario: FormularioDoAgendamento;
    }) => {
      if (!api) throw new Error('Sem sessão.');
      // Remarcando, "Manter o horário atual" escolhe a vaga de agora, que não está entre as livres.
      const mantida = remarcando && vagaId === vagaAtual && consulta.consulta_em !== null;
      const vaga = mantida
        ? { id: vagaId, data_hora: '' }
        : (vagas.data?.lista.find((v) => v.id === vagaId) ?? null);
      const consultaEm = mantida ? consulta.consulta_em : vaga ? instanteDaVaga(vaga) : null;
      const codigoDoPaciente = formulario.semCadastro ? null : codigoVinculado;
      if (!escolha || !vaga || !consultaEm || (!formulario.semCadastro && !codigoDoPaciente)) {
        throw new Error('Falta escolher o paciente e o horário.');
      }

      const mudou =
        escolha.medico.id !== consulta.medico?.id ||
        escolha.especialidade.id !== consulta.especialidade?.id;
      // A troca que se desfaz se o agendar falhar: só a de uma consulta já marcada.
      let trocada: ConsultaDaOportunidade | null = null;
      if (mudou) {
        try {
          const salva = await api.salvarConsultaDaOportunidade({
            caminho: { id: oportunidadeId },
            corpo: {
              medico_id: escolha.medico.id,
              especialidade_id: escolha.especialidade.id,
              consulta_em: consulta.consulta_em,
              versao: consulta.versao,
            },
          });
          cache.setQueryData(chaves.consultaDaOportunidade(oportunidadeId), salva);
          if (consultaMarcada(consulta)) trocada = salva;
        } catch (erro) {
          throw new FichaNaoGravada(erro);
        }
      }

      const parametros = parametrosDoAgendamento(formulario, {
        idAgenda: vaga.id,
        codigoDoPaciente,
        codigoDoMedico: escolha.medico.codigo,
        codigoDaEspecialidade: escolha.especialidade.codigo,
      });
      /*
       * Uma chave por vaga: se a primeira resposta se perdeu, o servidor devolve
       * o agendamento gravado em vez de achar a vaga ocupada pelo próprio pedido.
       * Mudar um campo depois disso o servidor recusa (IDEMPOTENCIA_DIVERGENTE),
       * e a tela relê a consulta para mostrar o que ficou.
       */
      const chave = chavesDoAgendamento.current.get(vaga.id) ?? novaChave();
      chavesDoAgendamento.current.set(vaga.id, chave);
      try {
        if (remarcando) {
          return await api.remarcarConsulta({
            caminho: { id: oportunidadeId },
            corpo: {
              operacao_id: operacoes.remarcar!,
              parametros,
              agendamento: { codigo_da_agenda: vaga.id, consulta_em: consultaEm },
              // A troca do médico, antes, gravou outra versão: é a dela que o servidor confere.
              versao: (trocada ?? consulta).versao!,
            },
            idempotencyKey: chave,
          });
        }
        return await api.agendarConsulta({
          caminho: { id: oportunidadeId },
          corpo: {
            operacao_id: operacoes.agendar,
            parametros,
            agendamento: {
              codigo_do_paciente: codigoDoPaciente,
              codigo_da_agenda: vaga.id,
              consulta_em: consultaEm,
            },
          },
          idempotencyKey: chave,
        });
      } catch (erro) {
        if (trocada) await desfazerATroca(consulta, trocada);
        throw erro;
      }
    },
    onSuccess: (marcada) => {
      cache.setQueryData(chaves.consultaDaOportunidade(oportunidadeId), marcada);
      void cache.invalidateQueries({ queryKey: chaves.vagasDaConsulta(medicoId) });
      // Remarcada, a vaga de agora é outra: o "Editar" seguinte relê o que ficou gravado.
      void cache.invalidateQueries({ queryKey: chaves.agendamentoDaConsulta(oportunidadeId) });
      // O paciente vinculado no agendamento aparece no "Paciente no SISAC" sem recarregar (#1187).
      if (consulta.cliente_id) {
        void cache.invalidateQueries({ queryKey: chaves.pacienteDoCliente(consulta.cliente_id) });
      }
      aoMarcar(marcada.conferencia);
    },
    onError: (erro) => {
      if (conflito(erro)) {
        definirOcupada(true);
        recarregarVagas();
      }
      if (divergente(erro)) {
        void cache.invalidateQueries({ queryKey: chaves.consultaDaOportunidade(oportunidadeId) });
      }
    },
  });

  const erroDasVagas = vagas.isError
    ? vagas.error
    : porMes && diasDoMes.isError
      ? diasDoMes.error
      : null;
  const estadoDasVagas: EstadoDasVagas =
    erroDasVagas !== null
      ? {
          situacao: 'erro',
          recado: recadoDaLeitura(erroDasVagas, 'Não foi possível buscar os horários.'),
        }
      : porMes
        ? // Por mês, o calendário fica à vista enquanto o mês e o dia carregam.
          {
            situacao: 'pronta',
            lista: dia !== null && vagas.data ? vagasNaTela(vagas.data.lista) : [],
            cortada: dia !== null && (vagas.data?.cortada ?? false),
            ocupada,
          }
        : vagas.data
          ? {
              situacao: 'pronta',
              lista: vagasNaTela(vagas.data.lista),
              cortada: vagas.data.cortada,
              ocupada,
            }
          : { situacao: 'carregando' };
  const hojeMes = mesDe(new Date());
  const calendarioPorMes: CalendarioPaginado | null = porMes
    ? {
        mes,
        aoMudarMes: (novo: string) => {
          definirMes(novo);
          definirDia(null);
        },
        diasComVaga: diasDoMes.data ?? {},
        carregandoMes: diasDoMes.isPending,
        dia,
        aoAbrirDia: definirDia,
        carregandoDia: dia !== null && vagas.isPending,
        primeiroMes: hojeMes,
        ultimoMes: mesesDepois(hojeMes, MESES_A_FRENTE),
      }
    : null;

  const estadoDoEnvio: EstadoDoEnvio = marcar.isPending
    ? { situacao: 'enviando' }
    : !marcar.isError
      ? { situacao: 'parado' }
      : marcar.error instanceof FichaNaoGravada
        ? conflito(marcar.error.causa)
          ? { situacao: 'conflito' }
          : {
              situacao: 'erro',
              recado: recadoDeErro(marcar.error.causa, { generica: marcar.error.message }),
            }
        : foraDoAr(marcar.error)
          ? { situacao: 'fora-do-ar' }
          : conflito(marcar.error)
            ? // A vaga tomada aparece na parte Vaga, com a tabela relida.
              { situacao: 'parado' }
            : divergente(marcar.error)
              ? {
                  situacao: 'erro',
                  recado:
                    'Este horário já foi enviado com outros dados. Feche e abra a consulta para ver se ele ficou agendado.',
                }
              : {
                  situacao: 'erro',
                  recado: recadoDaEscrita(
                    recadoDeErro(marcar.error, {
                      generica: 'Não foi possível agendar a consulta.',
                    }),
                    marcar.error,
                  ),
                };

  return {
    paciente,
    vagas: estadoDasVagas,
    porMes: calendarioPorMes,
    remarcacao: remarcando
      ? {
          vagaAtual: vagaAtual!,
          consultaEm: consulta.consulta_em,
          medicoId: consulta.medico?.id ?? null,
          dados: lido.data?.dados ?? null,
          // Sem a leitura ligada, o formulário começa só com o cadastro e os padrões.
          carregando: operacoes.ler_agendamento !== undefined && lido.isPending,
        }
      : null,
    padroes,
    envio: estadoDoEnvio,
    listas: {
      convenios: estadoDaLista('convenios', convenios),
      planos: estadoDaLista('planos', planos, convenio === ''),
      origens: estadoDaLista('origens', origens),
      locais: estadoDaLista('locais', locais),
    },
    aoVerVagas: ({ medicoId: idDoMedico, especialidadeId }: EscolhaDoMedico) => {
      const medico = opcoes.medicos.find((m) => m.id === idDoMedico);
      const especialidade = opcoes.especialidades.find((e) => e.id === especialidadeId);
      if (!medico || !especialidade) return;
      marcar.reset();
      definirOcupada(false);
      definirEscolha({ medico, especialidade });
      definirMes(mesDe(new Date()));
      definirDia(null);
    },
    // A especialidade trocada no passo 3 (#1187): o médico, o mês e o horário ficam.
    aoTrocarEspecialidade: (especialidadeId: string) => {
      const especialidade = opcoes.especialidades.find((e) => e.id === especialidadeId);
      if (especialidade) definirEscolha((atual) => (atual ? { ...atual, especialidade } : atual));
    },
    aoEscolherConvenio: definirConvenio,
    aoRecarregarVagas: () => {
      definirOcupada(false);
      recarregarVagas();
    },
    aoMarcar: (vagaId: string, formulario: FormularioDoAgendamento) =>
      marcar.mutate({ vagaId, formulario }),
  };
}
